import React, { useEffect, useState } from "react";
import { collection, onSnapshot, doc, updateDoc, getDoc, query, orderBy, addDoc } from "firebase/firestore";
import { auth, db } from "../../firebaseConfig";
import { Container, Card, Button, Badge, Modal, Spinner, Col, Row, Stack, Form, Alert } from "react-bootstrap";
import { Order, PaymentType } from "../../model/OrderModel";
import { useGlobal } from "../../contexts/GlobalContext";
import { Utils } from "../../common/Utils";
import { MessageGateway, MessageType, SendMessageRequest, WhatsAppTemplates } from "../../gateways/MessageGateway";

interface OrderDetails {
  id: string,
  order: Order,
  profileImage: string
}

const ManageOrders: React.FC = () => {
  const { settings } = useGlobal();
  const [orders, setOrders] = useState<OrderDetails[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrderDetails[]>([]);
  const [filteredStatus, setFilteredStatus] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [previousOrders, setPreviousOrders] = useState<string[]>([]);
  const [modalOrder, setModalOrder] = useState<OrderDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [orderPin, setOrderPin] = useState<boolean>(true);
  const [filter, setFilter] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showSetPaymentMethod, setShowSetPaymentMethod] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string>("");

  const statusVariants = [
    { status: "New", variant: "primary", actionText: "", filterText: "New", icon: "Package.png" },
    { status: "In-progress", variant: "outline-secondary", actionText: "Start Progress", filterText: "In Progress", icon: "Clock.png" },
    { status: "Out-for-Delivery", variant: "secondary", actionText: "Out for Delivery", filterText: "Out for Delivery", icon: "Delivery.png" },
    { status: "Completed", variant: "primary", actionText: "Complete", filterText: "Completed", icon: "Completed.png" },
    { status: "Cancelled", variant: "danger", actionText: "Cancel", filterText: "Cancelled", icon: "Cancelled.png" }
  ];

  useEffect(() => {
    const ordersRef = query(collection(db, "orders"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(ordersRef, async (snapshot) => {
      try {
        const ordersList = await Promise.all(
          snapshot.docs.map(async (orderDoc) => {
            const orderData = orderDoc.data() as Order;
            var profileImage: string = "";
            if (orderData && orderData.userId) {
              const userRef = doc(db, "users", orderData.userId);
              const userSnap = await getDoc(userRef);
              profileImage = userSnap.exists() ? userSnap.data().profileImage : undefined
            }

            return {
              id: orderDoc.id,
              order: orderData,
              updatedAt: orderData.updatedAt ?? orderData.createdAt,
              profileImage
            };
          })
        );

        const today = new Date();
        const filteredOrders = ordersList.filter(({ order, updatedAt }) => {
          const status = order.status?.toLowerCase();
          if (!startDate || !endDate || !filter) {
            if (status === "cancelled" || status === "completed" || status === "paymentpending") {
              return updatedAt && Utils.IsSameDay(new Date(updatedAt), today);
            }
          }
          else {
            return (new Date(updatedAt) >= (startDate ?? today)) && (new Date(updatedAt) <= (endDate ?? today));
          }
          return true;
        });

        // Check for new orders
        const newOrderIds = filteredOrders.map(order => order.id);
        const newOrders = newOrderIds.filter(id => !previousOrders.includes(id));
        const countPendingPayment = orders.filter(order => newOrders.includes(order.id) && order.order.status === "PaymentPending").length;

        if (newOrders.length > 0 && !loading && hasInteracted && countPendingPayment < newOrderIds.length) {
          try {
            const notificationSound = new Audio("/notification.mp3"); // Ensure this file is available in your public folder
            notificationSound.play();
          }
          catch (err: any) {
            console.error("Error playing sound", err);
          }
        }

        setPreviousOrders(newOrderIds);
        setOrders(filteredOrders);

        // Update status counts dynamically
        const counts = filteredOrders.reduce(
          (acc, order) => {
            acc[order.order.status] = (acc[order.order.status] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>
        );
        setStatusCounts(counts);
        setLoading(false);
      }
      catch (err) {
        console.error("Error getting orders", err);
        setLoading(false);
      }
    });
    return () => unsubscribe(); // Cleanup listener
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, startDate, endDate, filter, hasInteracted]);

  // Automatically filter orders when filteredStatus is updated
  useEffect(() => {
    if (!filteredStatus) {
      setFilteredOrders(orders.filter(order => order.order.status !== "PaymentPending"));
    } else {
      setFilteredOrders(orders.filter(order => order.order.status === filteredStatus));
    }
  }, [filteredStatus, orders]);

  useEffect(() => {
    if (settings)
      setOrderPin(settings.orderPin);
  }, [settings]);

  useEffect(() => {
    const handleInteraction = () => setHasInteracted(true);

    window.addEventListener("click", handleInteraction, { once: true });

    return () => {
      window.removeEventListener("click", handleInteraction);
    };
  }, []);


  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Fetching orders...</p>
      </Container>
    );
  }

  const generateOrderStatusMessage = (order: OrderDetails, status: string) => {
    return `The status of your order (#${order.order.orderNumber}) has been updated to ${status}.`;
  }

  const handleStatusChange = async (order: OrderDetails, status: string) => {

    const notPaid = (order.order.paymentMethod === PaymentType.OnCollection && order.order.status !== "Completed") || order.order.status === "PaymentPending" || (Number(order.order.totalAmount) < Number(order.order.totalPaid));

    if (settings.hasPrice && notPaid && status === "Completed") {
      if (!showSetPaymentMethod && settings.checkoutPaymentOptions && settings.checkoutPaymentOptions.filter(option => option !== "On-Collection").length > 1) {
        setModalOrder(order);
        setShowSetPaymentMethod(true);
        return;
      }
      else {
        setShowSetPaymentMethod(false);
        setModalOrder(null);
        order.order.finalPaymentMethod = paymentMethod;
        order.order.totalPaid = order.order.totalAmount;
        setPaymentMethod("");
      }
    }

    setError(null);
    const user = auth.currentUser;
    await updateDoc(doc(db, "orders", order.id), { status, finalPaymentMethod: order.order.finalPaymentMethod ?? "", totalPaid: order.order.totalPaid ?? "0.00" , updatedAt: new Date().toISOString() });
    await addDoc(collection(db, "orderLogs"), {
      order: order.id,
      userId: user?.uid,
      timestamp: new Date().toISOString(), // Firebase auto-generates server time
      status,
      paymentMethod
    });

    try {      
        const token = await user?.getIdToken();
        const messageRequest: SendMessageRequest = { toAddress: order.order.messagePreference === MessageType.Email ? order.order.email : order.order.phoneNumber, messageType: order.order.messagePreference, message: generateOrderStatusMessage(order, status), subject: `${settings.siteTitle} - Order Update`, whatsappTemplate: WhatsAppTemplates.NotifyOrderStatusChange, whatsappTemplateParams: { "1": order.order.orderNumber, "2": status } };
        MessageGateway.SendMessage(messageRequest, token ?? "", Utils.GetAlias());
      
    }
    catch (err) {
      console.error("Failed to send notification message", err)
      setError("Failed to send the order's notification message");
    }

    if (status === "In-progress" && orderPin) {
      setModalOrder(order);
    }
    else if (modalOrder) {
      setModalOrder(null);
    }
  }

  const handleOrderPinChange = () => {
    updateDoc(doc(db, "website", "settings"), { orderPin: !orderPin });
    setOrderPin(!orderPin);
  };

  const getOrderCardById = (orderId: string) => {
    return getOrderCard(orders.find(o => o.id === orderId));
  }

  const getPaymentBadge = (order: OrderDetails) => {
    if (settings.hasPrice) {
      const notPaid = (order.order.paymentMethod === PaymentType.OnCollection && order.order.status !== "Completed") || order.order.status === "PaymentPending";
      return (<Badge bg={notPaid ? 'danger' : order.order.totalAmount <= order.order.totalPaid ? 'success' : 'warning'}>{notPaid ? 'Not Paid' : order.order.totalAmount <= order.order.totalPaid ? 'Paid' : 'Partially paid'}</Badge>);
    }
  }

  const getOrderCard = (order: OrderDetails | undefined) => {
    //const minutesAgo = order ? Utils.DifferenceInMinutes(new Date(), new Date(order.order.createdAt.toLocaleString())) : 0;

    return (order && <Card key={order.id} className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center">
          {/* Left-aligned Order Details */}
          <div className="text-start">
            <div className="d-flex align-items-center gap-2">
              <h5 className="mb-0">Order #{order.order.orderNumber}</h5>
              {getPaymentBadge(order)}
            </div>
            <div>
              <strong>Status:</strong> {order.order.status}<br />
              <strong>Created:</strong> {new Date(order.order.createdAt).toLocaleString()}<br />
              <strong>Delivery Location:</strong> {order.order.deliveryLocation ? order.order.deliveryLocation : "Collection"}<br />
              {order.order.finalPaymentMethod && <><strong>Payment Method:</strong> {order.order.finalPaymentMethod}</>}
            </div>
          </div>

          {/* Right-aligned User Details */}
          <div className="text-end d-flex align-items-center gap-3">
            {order.profileImage && <img src={order.profileImage} alt="Profile" className="rounded-circle me-3" width="60" height="60" />}
            <div>
              <h5>{`${order.order.firstName} ${order.order.lastName}`}</h5>
              <p className="d-flex gap-3">{order.order.phoneNumber && <><a href={`whatsapp://send?phone=${order.order.phoneNumber}`} target="_blank" rel="noreferrer"><img alt={order.order.phoneNumber} style={{ height: "25px" }} src="/whatsapp.png" /></a><a href={`sms:${order.order.phoneNumber}`} target="_blank" rel="noreferrer"><img alt={order.order.phoneNumber} style={{ height: "28px" }} src="/sms.png" /></a></>}{order.order.email && <a href={`mailto:${order.order.email}`}><img style={{ height: "25px" }} alt={order.order.email} src="/email.png" /></a>}</p>
            </div>
          </div>
        </div>
        <hr />
        <strong>Order Items:</strong>

        {order.order.cartItems.map((item, index) => (
          <Row key={index} className="mb-2 ps-3">
            <Col xs={1} className="text-end pe-1">
              <span className="fw-bold">{item.quantity}</span>
            </Col>
            <Col xs={10}>
              <div>{item.name}</div>

              {item.attributes && Object.entries(item.attributes).length > 0 && (
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {Object.entries(item.attributes).map(([key, value]) => (
                    <Badge
                      key={key}
                      bg="light"
                      text="dark"
                      style={{ fontSize: "0.95rem" }}
                      className="px-3 py-1 border border-secondary-subtle rounded"
                    >
                      {key}: {value}
                    </Badge>
                  ))}
                </div>
              )}
            </Col>
            {settings.hasPrice && <Col xs={1} className="text-end">
              <div>{Utils.FormatCurrency(Number(item.priceAfterTax) * item.quantity, settings.currency)}</div>
            </Col>}
          </Row>
        ))}
        {settings.hasPrice && <>
          {settings.allowDelivery &&
            <Row className="mb-2 ps-3">
              <Col xs={1}>
              </Col>
              <Col xs={10} className="text-end pe-1"><span className="fw-bold">Delivery Fee:</span></Col>
              <Col xs={1} className="text-end">{Utils.FormatCurrency(Number(order.order.deliveryFee), settings.currency)}</Col>
            </Row>}
          <Row className="mb-2 ps-3">
            <Col xs={1}>
            </Col>
            <Col xs={10} className="text-end pe-1"><span className="fw-bold">Total:</span></Col>
            <Col xs={1} className="text-end">{Utils.FormatCurrency(order.order.cartItems.reduce((sum, item) => sum + item.quantity * Number(item.priceAfterTax), 0) + Number(order.order.deliveryFee), settings.currency)}</Col>
          </Row>
          {order.order.totalPaid && Number(order.order.totalAmount) > 0 && <Row className="mb-2 ps-3">
            <Col xs={1}>
            </Col>
            <Col xs={10} className="text-end pe-1"><span className="fw-bold">Total Paid:</span></Col>
            <Col xs={1} className="text-end">{Utils.FormatCurrency(Number(order.order.totalPaid), settings.currency)}</Col>
          </Row>}
        </>}
        <hr />
        <p><strong>Comments:</strong> {order.order.comments || "No comments"}</p>


        <div className="d-flex gap-2">
          {statusVariants
            .map(({ status, variant, actionText }, index) => {
              //treat PendingPayment as New
              const orderStatus = order.order.status === "PaymentPending" ? "New" : order.order.status;
              const lookupIndex = statusVariants.findIndex(i => i.status === orderStatus);
              const shouldShowButton = index > lookupIndex &&
                lookupIndex <= 1 && (order.order.deliveryLocation || status !== "Out-for-Delivery");
              return (
                <Button
                  key={status}
                  variant={variant}
                  onClick={() => handleStatusChange(order, status)}
                  className="w-100"
                  style={{ display: shouldShowButton ? "block" : "none" }}
                >
                  {actionText}
                </Button>
              )
            })}
        </div>

      </Card.Body>
    </Card>);
  }

  return (
    <>
      <style>{`
        .transparent-button {
          background-color: transparent !important;
        }
      `}</style>
      <Container className="mt-3">
        {error && <Alert variant="danger">{error}</Alert>}
        <Row className="g-2 mb-2">
          {statusVariants.map(({ status, filterText, icon }) => (
            <Col xs={12} sm={6} md={2} lg={2} key={status}>
              <Button
                key={status}
                className="d-flex align-items-center gap-2 w-100 text-center align-items-center flex-column"
                variant={filteredStatus === status ? "primary" : "outline-secondary"}
                onClick={() =>
                  setFilteredStatus(filteredStatus === status ? null : status)
                }
              >
                <div>
                  <img src={`/${icon}`} alt={status} style={{ width: "22px" }} />
                  &nbsp;{filterText} ({statusCounts[status] ?? 0})</div>
              </Button>
            </Col>
          ))}

          {/* {statusVariants.map(({ status, variant }) => (
            <Button
              style={{ width: "150px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              key={status}
              variant={filteredStatus === status ? variant : `outline-${variant}`}
              onClick={() => setFilteredStatus(filteredStatus === status ? null : status)}
            >
              <span className="text-start">{status}</span>
              <Badge bg="secondary">{statusCounts[status] || 0}</Badge>
            </Button>
          ))} */}
          <Col xs={12} sm={6} md={4} lg={2}>
            <Stack direction="horizontal" gap={2}>

              <Button
                onClick={handleOrderPinChange}
                className="d-flex justify-content-center align-items-center p-2 transparent-button"
                style={{
                  width: "50px",
                  border: orderPin ? "1px solid black" : "none",
                  display: "flex", justifyContent: "space-between", alignItems: "center",

                }}
              >
                <img src="/pin.png" alt="Selectable" style={{ maxWidth: "100%", height: "24px" }} />
              </Button>
              <Button
                onClick={() => setFilter(!filter)}
                className="d-flex justify-content-center align-items-center p-2 transparent-button"
                style={{
                  width: "50px",
                  border: filter ? "1px solid black" : "none",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}
              >
                <img src="/filter.png" alt="Selectable" style={{ maxWidth: "100%", height: "24px" }} />
              </Button>
            </Stack>
          </Col>
        </Row>

        {filter && (
          <Row className="mb-3">
            <Col xs={12} sm={6} md={4} lg={3}>
              <Form.Group controlId="startDate">
                <Stack direction="horizontal" gap={2}>
                  <Form.Label className="mb-0" style={{ minWidth: "85px" }}>
                    Start Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate ? startDate.toISOString().split("T")[0] : ""}
                    onChange={(e) => setStartDate(new Date(e.target.value))}
                  />
                </Stack>
              </Form.Group>
            </Col>

            <Col xs={12} sm={6} md={4} lg={3}>
              <Form.Group controlId="endDate">
                <Stack direction="horizontal" gap={2}>
                  <Form.Label className="mb-0" style={{ minWidth: "85px" }}>
                    End Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate ? endDate.toISOString().split("T")[0] : ""}
                    onChange={(e) => setEndDate(new Date(e.target.value))}
                  />
                </Stack>
              </Form.Group>
            </Col>
            {settings.hasPrice &&
              <Col xs={12} sm={6} md={4} lg={2}>
                <Form.Group controlId="pendingpayment">
                  <Button
                    className="d-flex align-items-center gap-2 w-100 text-center align-items-center flex-column"
                    variant={filteredStatus === "PaymentPending" ? "primary" : "outline-secondary"}
                    onClick={() =>
                      setFilteredStatus(filteredStatus === "PaymentPending" ? null : "PaymentPending")
                    }
                  >
                    <div>
                      <img src="Payment.png" alt="PaymentPending" style={{ width: "22px" }} />
                      &nbsp;No Payment ({statusCounts["PaymentPending"] ?? 0})</div>
                  </Button>
                </Form.Group>
              </Col>
            }
          </Row>
        )}


        {filteredOrders.map((order) => getOrderCard(order))}

        {/* Modal for In-progress Orders */}
        {modalOrder && !showSetPaymentMethod && (
          <Modal size="xl" show={true} onHide={() => setModalOrder(null)} centered>

            <Modal.Header closeButton>
              <Modal.Title>Order</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {getOrderCardById(modalOrder.id)}
            </Modal.Body>

          </Modal>
        )}

        <Modal show={showSetPaymentMethod} onHide={() => { setShowSetPaymentMethod(false); setModalOrder(null); }} centered>
          <Modal.Header closeButton>
            <Modal.Title>Set Payment Method</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <h5>Order #{modalOrder?.order.orderNumber}</h5>
              <Form.Group controlId="paymentMethodSelect">
                <Form.Label>Payment Method</Form.Label>
                <Form.Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="" disabled></option>
                  {settings.checkoutPaymentOptions.filter(option => option !== "On-Collection").map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowSetPaymentMethod(false); setModalOrder(null); }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => {
              modalOrder && handleStatusChange(modalOrder, "Completed")
            }} disabled={!paymentMethod}>
              Complete
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default ManageOrders;